import { createContext, useCallback, useContext, ReactNode, useMemo, useState } from "react"
import { LocaleProvider } from "@chakra-ui/react"
import { IntlProvider } from "react-intl"
import { messages } from "../locales"

const DEFAULT_LOCALE = "en"

interface LocaleContextType {
    locale: string
    setLocale: (locale: string) => void
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined)

/**
 * `locale` reaches us from the user profile, so a malformed value would
 * otherwise reach `Intl` and throw during render — taking the whole app down
 * with a blank screen rather than just falling back to English.
 */
function isSupportedLocale(value: string): boolean {
    if (!value) return false
    try {
        Intl.getCanonicalLocales(value)
    } catch {
        return false
    }
    return value in messages
}

export function AppLocaleProvider({ children }: { children: ReactNode }) {
    const [locale, setLocaleState] = useState(DEFAULT_LOCALE)

    const setLocale = useCallback((next: string) => {
        setLocaleState(isSupportedLocale(next) ? next : DEFAULT_LOCALE)
    }, [])

    const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale])

    return (
        <LocaleContext.Provider value={value}>
            <IntlProvider
                locale={locale}
                messages={messages[locale] as Record<string, string>}
                defaultLocale={DEFAULT_LOCALE}
            >
                <LocaleProvider locale={locale}>
                    {children}
                </LocaleProvider>
            </IntlProvider>
        </LocaleContext.Provider>
    )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useLocale() {
    const context = useContext(LocaleContext)
    if (!context) {
        throw new Error("useLocale must be used within an AppLocaleProvider")
    }
    return context
}